"use client";

import { useMemo } from "react";
import { countActiveManualUserTasksFromPage } from "@/lib/user-tasks/count-active-user-tasks";
import { filterManualUserTasks } from "@/lib/user-tasks/is-manual-user-task";
import { sortTodoWidgetTasks } from "@/lib/user-tasks/sort-todo-widget-tasks";
import type { GetUserTaskResponse, ToDoTaskRow } from "@/openapi/generated";
import { useUserTasksGet } from "./useUserTasksGet";

/** Fetch enough active tasks to sort sensibly before the widget trims to 4. */
const TODO_WIDGET_PAGE_SIZE = 50;

export const TODO_WIDGET_PREVIEW_LIMIT = 4;

function toToDoTaskRow(task: GetUserTaskResponse): ToDoTaskRow {
  return {
    id: task.id,
    title: task.title,
    description: task.description,
    status: task.status,
    dueAt: task.dueAt,
    sourceModule: task.sourceModule,
    sourceReference: task.sourceReference,
    createdAt: task.createdAt,
  };
}

/**
 * Dashboard My Tasks widget — same source as the sidebar badge and My Tasks page
 * (`GET /api/user-tasks`), not the widgets/data ToDo provider (can diverge or parse empty).
 */
export function useToDoWidget(enabled = true) {
  const query = useUserTasksGet({
    activeOnly: true,
    pageSize: TODO_WIDGET_PAGE_SIZE,
    enabled,
  });

  const manualItems = useMemo(
    () => filterManualUserTasks(query.data?.items ?? []),
    [query.data?.items],
  );

  const data = useMemo(
    () => sortTodoWidgetTasks(manualItems.map(toToDoTaskRow)),
    [manualItems],
  );

  const activeCount = useMemo(
    () => countActiveManualUserTasksFromPage(query.data),
    [query.data],
  );

  return {
    data,
    activeCount,
    isError: query.isError,
    isSuccess: query.isSuccess,
    isPending: query.isPending,
    refetch: query.refetch,
  };
}
